import {
  ESymbolCategory,
  EWFStepType,
  IInteractionParameter,
  IStartProcessTrigger,
  ISymbol,
  IUserInteraction,
  IWFNodes,
  IWFParameter,
  IWFStep,
  IWorkFlow
} from "@kuika/kuika-cl-model";

/**
 * The user-task (ScreenInteraction) steps of a single process, in declaration order, walking
 * into subprocesses so nested user tasks surface too. This is the set of steps the Forms tab
 * builds a screen for — deterministic (no wizard-style re-sorting) so the list stays stable as
 * the process is edited.
 */
export const getUserTaskSteps = (workflow: IWorkFlow | undefined): IWFStep[] => {
  if (!workflow?.nodes) return [];

  const collect = (nodes: IWFNodes): IWFStep[] => {
    const steps: IWFStep[] = [];
    for (const step of nodes.steps ?? []) {
      if (step.stepType === EWFStepType.ScreenInteraction) {
        steps.push(step);
      }
      if (step.stepType === EWFStepType.Subprocess && step.subprocess) {
        steps.push(...collect(step.subprocess));
      }
    }
    return steps;
  };

  return collect(workflow.nodes);
};

/**
 * The ids of the workflow parameters this step configures as *outputs*. A form field bound to
 * one of these is fully locked in the UI (pre-checked, cannot be removed, made optional, or made
 * read-only) — the user must fill it because the step writes it back. The id maps to the field's
 * `linkedWorkflowBoParameterId`.
 */
export const getStepOutputParameterIds = (step: IWFStep | undefined): Set<string> =>
  new Set((step?.outputParameters ?? []).map((parameter): string => parameter.id));

/**
 * The step's output parameters resolved to their workflow-parameter metadata (scalars only —
 * object-typed parameters can't carry a fixed value or a form field). Feeds the Step Outputs chip
 * strip on both sub-tabs and the per-interaction parameter tables.
 */
export const getStepOutputParams = (workflow: IWorkFlow | undefined, step: IWFStep | undefined): IWFParameter[] => {
  if (!workflow || !step) return [];
  const ids: Set<string> = getStepOutputParameterIds(step);
  return (workflow.parameters ?? []).filter((p: IWFParameter): boolean => ids.has(p.id) && p.isObject !== true);
};

/**
 * True when an interaction-parameter symbol carries a usable value for a notification trigger.
 * A non-fixed category is a real binding resolved at runtime → set. A fixed value counts unless
 * it is null/undefined or a blank string — the empty string is exactly the "can stay empty,
 * filled from the form" state the Interactions editor seeds for Business Object parameters
 * (numbers/booleans/dates are always concrete, so any non-null fixed value counts).
 */
const hasProvidedValue = (value: ISymbol | undefined): boolean => {
  if (!value || value.category === ESymbolCategory.none) return false;
  if (value.category !== ESymbolCategory.fixed) return true;
  const fixed: unknown = value.fixedValue;
  if (fixed === null || fixed === undefined) return false;
  if (typeof fixed === "string") return fixed.trim() !== "";
  return true;
};

/**
 * True when the interaction may be offered on a NOTIFICATION surface — the "Add User
 * Interactions to Mail" multiselect and the custom-template symbol picker's "… Mail Action Link"
 * entries. A notification trigger completes the step with NO form to fill Business Object
 * outputs from, so every BO output of the step must carry a provided value on the interaction;
 * otherwise the triggered step would write the BO back with empty values. Internal outputs
 * already carry mandatory fixed values, and an interaction predating a BO output (no parameter
 * entry at all — the pre-Forms-tab modal only stored internal params) is equally ineligible.
 */
export const isInteractionNotifiable = (
  workflow: IWorkFlow | undefined,
  step: IWFStep | undefined,
  interaction: IUserInteraction
): boolean => {
  const boOutputs: IWFParameter[] = getStepOutputParams(workflow, step).filter(
    (p: IWFParameter): boolean => p.isBusinessObject === true
  );
  return boOutputs.every((p: IWFParameter): boolean => {
    const entry: IInteractionParameter | undefined = interaction.parameters?.find(
      (x: IInteractionParameter): boolean => x.id === p.id
    );
    return hasProvidedValue(entry?.value);
  });
};

/**
 * True when the process cannot be started as the project stands: it declares at least one INPUT
 * parameter (a caller has to hand values in) and nothing in the project — no Screen, ScheduledJob
 * or ApiManagement action — calls StartProcess against it. `triggers` is the process's slice of
 * `IProject.startProcessTriggers`, the reverse index the trigger sync hooks maintain, read as-is:
 * no orphan sweep here, because that write-back also drops every job/API trigger whose list is not
 * loaded in the current tool. After Apply Screens the Forms tab offers to generate a start screen
 * while this holds (KOA-18746).
 */
export const needsStartScreen = (
  workflow: IWorkFlow | undefined,
  triggers: readonly IStartProcessTrigger[] | undefined
): boolean => {
  if (!workflow?.id) return false;
  const takesInput: boolean = (workflow.parameters ?? []).some((p: IWFParameter): boolean => p.isInput === true);
  return takesInput && (triggers?.length ?? 0) === 0;
};

/**
 * The generated start screen's name: "Start <process>", from the display name when there is one,
 * else the name; undefined (the backend's default) when the process has neither.
 */
export const startScreenNameFor = (workflow: IWorkFlow | undefined): string | undefined => {
  const displayName: string = (workflow?.displayName ?? "").trim();
  const name: string = (workflow?.name ?? "").trim();
  const base: string = displayName || name;
  return base ? `Start ${base}` : undefined;
};
